import { IRxPlayer } from 'rx-player/dist/es2017/main_thread/types';
import {
  ILoadVideoOptions,
  ILockedVideoRepresentationsSettings,
  IPlayerState,
} from 'rx-player/types';

import {
  IAudioTrackSetting,
  IAvailableAudioTrack,
  IAvailableTextTrack,
  IAvailableVideoTrack,
  IKeySystemConfigurationOutput,
  IParsedAudioTrack,
  IParsedTextTrack,
  IParsedVideoTrack,
  IPeriod,
  IPlayerCoreEventEmitterListener,
  IPlayerWrapper,
  IPlayerWrapperEvents,
  ITextTrackSetting,
  IVideoTrackSetting,
} from '@canalplus/oneplayer-types';
import { logger } from '@canalplus/oneplayer-utils';

export default class RxPlayerWrapper
  implements IPlayerWrapper<ILoadVideoOptions>
{
  private rxPlayer: IRxPlayer;

  get state(): IPlayerState {
    return this.rxPlayer.state;
  }

  get version(): string {
    return this.rxPlayer.version;
  }

  constructor(rxPlayer: IRxPlayer) {
    this.rxPlayer = rxPlayer;
  }

  addEventListener<TEventName extends keyof IPlayerWrapperEvents>(
    evt: TEventName,
    fn: IPlayerCoreEventEmitterListener<IPlayerWrapperEvents, TEventName>,
  ): void {
    if (evt === 'moralityLocked') {
      return;
    }
    type IRxPlayerAddEventListener = IRxPlayer['addEventListener'];
    this.rxPlayer.addEventListener(
      evt,
      fn as Parameters<IRxPlayerAddEventListener>[1],
    );
  }

  removeEventListener<TEventName extends keyof IPlayerWrapperEvents>(
    evt: TEventName,
    fn: IPlayerCoreEventEmitterListener<IPlayerWrapperEvents, TEventName>,
  ): void {
    if (evt === 'moralityLocked') {
      return;
    }
    type IRxPlayerRemoveEventListener = IRxPlayer['removeEventListener'];
    this.rxPlayer.removeEventListener(
      evt,
      fn as Parameters<IRxPlayerRemoveEventListener>[1],
    );
  }

  getAudioTrack(
    periodId?: string | undefined,
  ): IParsedAudioTrack | null | undefined {
    return this.rxPlayer.getAudioTrack(periodId);
  }

  getTextTrack(
    periodId?: string | undefined,
  ): IParsedTextTrack | null | undefined {
    // TODO: fix type on RxPlayer-side
    return this.rxPlayer.getTextTrack(periodId) as
      | IParsedTextTrack
      | null
      | undefined;
  }

  getVideoTrack(
    periodId?: string | undefined,
  ): IParsedVideoTrack | null | undefined {
    return this.rxPlayer.getVideoTrack(periodId);
  }

  getAvailableTextTracks(periodId?: string | undefined): IAvailableTextTrack[] {
    // TODO: fix type on RxPlayer-side
    return this.rxPlayer.getAvailableTextTracks(
      periodId,
    ) as IAvailableTextTrack[];
  }

  getAvailableVideoTracks(
    periodId?: string | undefined,
  ): IAvailableVideoTrack[] {
    return this.rxPlayer.getAvailableVideoTracks(periodId);
  }

  getAvailableAudioTracks(
    periodId?: string | undefined,
  ): IAvailableAudioTrack[] {
    return this.rxPlayer.getAvailableAudioTracks(periodId);
  }

  setAudioTrack(trackInfo: string | IAudioTrackSetting): void {
    return this.rxPlayer.setAudioTrack(trackInfo);
  }

  setTextTrack(trackInfo: string | ITextTrackSetting): void {
    return this.rxPlayer.setTextTrack(trackInfo);
  }

  disableTextTrack(periodId?: string | undefined): void {
    return this.rxPlayer.disableTextTrack(periodId);
  }

  setVideoTrack(trackInfo: string | IVideoTrackSetting): void {
    return this.rxPlayer.setVideoTrack(trackInfo);
  }

  getAvailablePeriods(): IPeriod[] {
    return this.rxPlayer.getAvailablePeriods();
  }

  getCurrentPeriod(): IPeriod | null | undefined {
    return this.rxPlayer.getCurrentPeriod();
  }

  getKeySystemConfiguration():
    | IKeySystemConfigurationOutput
    | null
    | undefined {
    return this.rxPlayer.getKeySystemConfiguration();
  }

  getContentUrls(): string[] | undefined {
    return this.rxPlayer.getContentUrls();
  }

  getMinimumPosition(): number | null | undefined {
    return this.rxPlayer.getMinimumPosition();
  }

  getMaximumPosition(): number | null | undefined {
    return this.rxPlayer.getMaximumPosition();
  }

  getLivePosition(): number | null | undefined {
    return this.rxPlayer.getLivePosition();
  }

  getPosition(): number {
    return this.rxPlayer.getPosition();
  }

  getWallClockTime(): number {
    return this.rxPlayer.getWallClockTime();
  }

  getVideoElement(): HTMLMediaElement | null {
    return this.rxPlayer.getVideoElement();
  }

  getMediaDuration(): number | undefined {
    const duration = this.rxPlayer.getMediaDuration();
    if (isNaN(duration)) {
      return undefined;
    }
    return duration;
  }

  getVideoBitrate(): number | undefined {
    return this.rxPlayer.getVideoRepresentation()?.bitrate;
  }

  getPlayerState(): string {
    return this.rxPlayer.getPlayerState();
  }

  getAvailablePlaybackRates(): number[] | undefined {
    return undefined;
  }

  getPlaybackRate(): number {
    return this.rxPlayer.getPlaybackRate();
  }

  setPlaybackRate(speed: number): void {
    return this.rxPlayer.setPlaybackRate(speed);
  }

  loadVideo(options: ILoadVideoOptions): void {
    return this.rxPlayer.loadVideo(options);
  }

  reload(
    reloadOptions?:
      | {
          reloadAt?: { position?: number; relative?: number };
          keySystems?: any[];
          autoPlay?: boolean;
        }
      | undefined,
  ): void {
    return this.rxPlayer.reload(reloadOptions);
  }

  play(): void {
    this.rxPlayer.play();
  }

  pause(): void {
    this.rxPlayer.pause();
  }

  stop(): void {
    this.rxPlayer.stop();
  }

  seekTo(
    val:
      | number
      | { relative: number }
      | { position: number }
      | { wallClockTime: number },
  ): void {
    this.rxPlayer.seekTo(val);
  }

  mute(): void {
    this.rxPlayer.mute();
  }

  unMute(): void {
    this.rxPlayer.unMute();
  }

  isMute(): boolean {
    return this.rxPlayer.isMute();
  }

  setVolume(volume: number): void {
    this.rxPlayer.setVolume(volume);
  }

  getVolume(): number {
    return this.rxPlayer.getVolume();
  }

  disableParentalLock(): void {
    logger.error('RxPlayerWrapper > Calling `disableParentalLock`');
  }

  lockVideoRepresentations(
    arg: string[] | ILockedVideoRepresentationsSettings,
  ): void {
    this.rxPlayer.lockVideoRepresentations(arg);
  }

  unlockVideoRepresentations(periodId?: string | undefined): void {
    this.rxPlayer.unlockVideoRepresentations(periodId);
  }

  getLockedVideoRepresentations(
    periodId?: string | undefined,
  ): string[] | null {
    return this.rxPlayer.getLockedVideoRepresentations(periodId);
  }

  createDebugElement(element: HTMLElement): { dispose(): void } {
    return this.rxPlayer.createDebugElement(element);
  }

  dispose(): void {
    this.rxPlayer.dispose();
  }
}
